import "@genius-tracker/design-system/tokens.css";
import "@genius-tracker/design-system/reset.css";

import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoadingState, ThemeProvider } from "@genius-tracker/design-system";
import { LocaleProvider, useLocale } from "@genius-tracker/i18n/react";
import type { Locale } from "@genius-tracker/i18n";
import { App } from "./App.js";
import { MapsSetupProvider } from "./map/setup.js";
import { SessionProvider } from "./session/SessionProvider.js";
import { RealtimeProvider } from "./realtime/RealtimeProvider.js";
import { AccessProvider } from "./lib/AccessProvider.js";
import { isPublicAuthPath } from "./auth/publicAuthPaths.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { ToastProvider } from "./components/Toast.js";
import { RateLimitToastBridge } from "./components/RateLimitToastBridge.js";
import { ModuleRefusalToastBridge } from "./components/ModuleRefusalToastBridge.js";
import {
  buildUnauthenticatedAwareMutationCache,
  buildUnauthenticatedAwareQueryCache,
} from "./lib/unauthenticatedHandler.js";
import { retryDelayForQuery, shouldRetryQuery } from "./lib/rateLimitRetry.js";

/**
 * E-BUNDLE-2 — the four catalogs used to be imported statically here, which
 * bundled ALL languages (~2.4 MB of JSON) into the web entry chunk even though
 * a session only ever renders one of them. `LocaleProvider` is now used
 * WITHOUT `catalogs`, so it takes the dynamic path in
 * `packages/i18n/src/react.tsx` (A-GTCC-LAZY1) and loads only the active
 * locale plus the `de` fallback, each as its own chunk. `apps/admin` has been
 * on this path since A-GTCC-LAZY1; the tests keep passing `catalogs`
 * explicitly and stay fully synchronous.
 */

/**
 * E-BUNDLE-10 — der Public-Auth-Ast haengt nicht mehr am Entry-Chunk.
 *
 * `isPublicAuthPath` kommt aus `auth/publicAuthPaths.ts` und bleibt
 * synchron; der Router selbst (RegisterPage, VerifyEmailPages,
 * AcceptInvitePage, AccountDeletionPage, PublicAuthShell und
 * public-auth-api, zusammen rund 40 kB Quelltext) wird erst geladen,
 * wenn die URL wirklich ein oeffentlicher Auth-Pfad ist. Der
 * angemeldete Normalfall — jeder Start auf /dashboard, /map und allem
 * anderen — zahlt diese Bytes nicht mehr. Auf den oeffentlichen Pfaden
 * kostet es genau einen zusaetzlichen Roundtrip, den `Suspense`
 * ueberbrueckt.
 */
const PublicAuthRouter = lazy(() =>
  import("./auth/PublicAuthRouter.js").then((m) => ({ default: m.PublicAuthRouter })),
);

/**
 * Der Fallback braucht `t` und muss deshalb INNERHALB des
 * LocaleProvider gerendert werden — `main.tsx` hat auf Top-Level
 * keine Uebersetzungsfunktion.
 */
function PublicAuthFallback() {
  const { t } = useLocale();
  return <LoadingState label={t("common.loading")} />;
}

const defaultLocale = (import.meta.env.VITE_DEFAULT_LOCALE as Locale | undefined) ?? "en";

/**
 * GT-FI-MMP-COMPLETION-01 Wave 1 — wire the global mid-session 401
 * redirect through the QueryClient's query + mutation caches. Without
 * this, any HTTP 401 from a product API call surfaces as an error
 * state in the calling hook and the UI renders the raw
 * "[401] …: unauthenticated" message instead of re-authenticating.
 * See `apps/web/src/lib/unauthenticatedHandler.ts`.
 */
const queryClient = new QueryClient({
  queryCache: buildUnauthenticatedAwareQueryCache(),
  mutationCache: buildUnauthenticatedAwareMutationCache(),
  defaultOptions: {
    queries: {
      // GT-…-RATE-LIMIT-FIX-02 — replace the blind `retry: 1` (which
      // retried a 429 IMMEDIATELY, adding load to an over-budget
      // window) with a policy that never retries a 429 before its
      // `Retry-After`, never retries other 4xx, and keeps one backoff
      // retry for transient network/5xx errors. See `lib/rateLimitRetry`.
      retry: shouldRetryQuery,
      retryDelay: retryDelayForQuery,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root element");

// Sprint 12 Wave B — public auth paths (`/register`,
// `/verify-email-sent`, `/verify-email-required`) must render
// WITHOUT a session. We branch at the top of the tree so that
// SessionProvider (which auto-redirects to /v1/auth/login on a
// 401) never mounts for these paths.
const isPublic = isPublicAuthPath(window.location.pathname);

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LocaleProvider initialLocale={defaultLocale}>
            <ToastProvider>
              <RateLimitToastBridge />
              <ModuleRefusalToastBridge />
              <MapsSetupProvider>
                <BrowserRouter>
                  {isPublic ? (
                    <Suspense fallback={<PublicAuthFallback />}>
                      <PublicAuthRouter />
                    </Suspense>
                  ) : (
                    <SessionProvider>
                      <AccessProvider>
                        <RealtimeProvider>
                          <App />
                        </RealtimeProvider>
                      </AccessProvider>
                    </SessionProvider>
                  )}
                </BrowserRouter>
              </MapsSetupProvider>
            </ToastProvider>
          </LocaleProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
